import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';

import { toFormValue } from '../../enrichmentFieldMapping';

import { INITIAL_SCAN_DELAY_MS, MUTATION_DEBOUNCE_MS } from './EnrichmentFieldIndicators.constants';
import { findFieldTargets, removeCreatedNodes } from './EnrichmentFieldIndicators.utils';
import FieldIndicator from './FieldIndicator';

const EnrichmentFieldIndicators = ({
  containerRef,
  enrichmentValuesMap,
  form,
  isLoading,
  setInForm,
  validateField,
}) => {
  const [fieldWrappers, setFieldWrappers] = useState({});
  const observerRef = useRef(null);
  const timeoutRef = useRef(null);
  const createdNodesRef = useRef([]);

  const onApplyField = useCallback(
    (fieldName, value) => {
      const formValue = toFormValue(fieldName, value);

      setInForm(fieldName, formValue);
      validateField?.(fieldName, formValue);
    },
    [setInForm, validateField],
  );

  const enrichmentFieldNames = useMemo(
    () => Object.keys(enrichmentValuesMap),
    [enrichmentValuesMap],
  );

  useEffect(() => {
    const container = containerRef?.current;

    if (!container || enrichmentFieldNames.length === 0) {
      return;
    }

    const findLabels = () => {
      const labels = findFieldTargets(container, enrichmentFieldNames, createdNodesRef.current);

      setFieldWrappers(prev => {
        const prevKeys = Object.keys(prev).sort().join(',');
        const newKeys = Object.keys(labels).sort().join(',');

        if (prevKeys === newKeys) {
          return prev;
        }

        return labels;
      });
    };

    timeoutRef.current = setTimeout(findLabels, INITIAL_SCAN_DELAY_MS);

    observerRef.current = new MutationObserver(() => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(findLabels, MUTATION_DEBOUNCE_MS);
    });
    observerRef.current.observe(container, { childList: true, subtree: true });

    const createdNodes = createdNodesRef.current;

    return () => {
      observerRef.current?.disconnect();
      clearTimeout(timeoutRef.current);
      removeCreatedNodes(createdNodes);
    };
  }, [containerRef, enrichmentFieldNames]);

  if (isLoading || _.isEmpty(enrichmentValuesMap)) {
    return null;
  }

  return enrichmentFieldNames.map(fieldName => {
    const targetEl = fieldWrappers[fieldName];

    if (!targetEl) {
      return null;
    }

    const rawValue = _.get(form, fieldName);
    const currentValue = rawValue?.value ?? rawValue;

    const mapEntry = enrichmentValuesMap[fieldName];
    const enrichmentValue = mapEntry?.originalValue !== undefined ? mapEntry.value : mapEntry;
    const originalValue = mapEntry?.originalValue;

    return (
      <FieldIndicator
        currentValue={currentValue}
        enrichmentValue={enrichmentValue}
        fieldName={fieldName}
        key={fieldName}
        originalValue={originalValue}
        targetEl={targetEl}
        onApply={onApplyField}
      />
    );
  });
};

EnrichmentFieldIndicators.propTypes = {
  enrichmentValuesMap: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  setInForm: PropTypes.func.isRequired,
  containerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  isLoading: PropTypes.bool,
  validateField: PropTypes.func,
};

export default memo(EnrichmentFieldIndicators);
